import { useState, useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { MapPin } from 'lucide-react'
import { useSeeds } from '../hooks/useSeeds'
import { useBreakdown } from '../hooks/useAnalytics'
import { rangeToFilters } from '../components/analytics/FilterBar'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'

interface RecentVisit {
  seed: string
  /** Rendered as the secondary line — now a summary rather than one address. */
  email: string
  timestamp: string
  country: string
  city: string
}

interface Stats {
  seeds: number
  clients: number
  shares: number
  domains: number
}

function useStats() {
  const [stats, setStats] = useState<Stats | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    Promise.all([
      fetch('/api/seeds').then(r => r.json()),
      fetch('/api/clients').then(r => r.json()),
      fetch('/api/shares').then(r => r.json()),
      fetch('/api/allowed-domains').then(r => r.json()),
    ])
      .then(([seedsData, clientsData, sharesData, domainsData]) => {
        if (cancelled) return
        setStats({
          seeds: (seedsData.seeds || []).length,
          clients: (clientsData.clients || []).length,
          shares: (sharesData.shares || []).length,
          domains: (domainsData.domains || []).length,
        })
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [])

  return { stats, loading }
}

/**
 * Most recent activity per seed.
 *
 * Used to pull 200 raw events and deduplicate them client-side, which meant the
 * list silently dropped any seed whose last visit fell outside the newest 200
 * rows platform-wide — on a busy day, most of them. A `by=seed` breakdown
 * carries `lastSeen` per seed directly.
 */
function useRecentVisits() {
  const filters = useMemo(() => {
    const { from, to } = rangeToFilters(30)
    return { from, to }
  }, [])
  const { rows, loading, labels } = useBreakdown(filters, 'seed', undefined, 10)
  const countries = useBreakdown(filters, 'seed', 'country', 60)

  const visits = useMemo<RecentVisit[]>(() => {
    const topCountry = new Map<string, { country: string; city: string }>()
    for (const row of countries.rows) {
      if (row.key === null || topCountry.has(String(row.key))) continue
      topCountry.set(String(row.key), { country: String(row.subKey ?? ''), city: '' })
    }
    return rows
      .filter(r => r.key !== null)
      .sort((a, b) => Number(b.lastSeen) - Number(a.lastSeen))
      .slice(0, 10)
      .map(r => ({
        seed: String(r.key),
        email: `${r.uniqueUsers} user${r.uniqueUsers === 1 ? '' : 's'} · ${r.events.toLocaleString()} events`,
        timestamp: new Date(Number(r.lastSeen)).toISOString(),
        country: topCountry.get(String(r.key))?.country ?? '',
        city: '',
      }))
  }, [rows, countries.rows])

  void labels
  return { visits, loading: loading || countries.loading }
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 30) return `${days}d ago`
  const months = days / 30.44
  if (months < 12) return `${months.toFixed(1)}mo ago`
  const years = months / 12
  return `${years.toFixed(1)}y ago`
}

const statItems = [
  { key: 'seeds' as const, label: 'Seeds', href: '/seeds' },
  { key: 'clients' as const, label: 'Clients', href: '/users' },
  { key: 'shares' as const, label: 'Shares', href: '/shares' },
  { key: 'domains' as const, label: 'Access Rules', href: '/users' },
]

export function OverviewPage() {
  const { stats, loading: statsLoading } = useStats()
  const { visits, loading: visitsLoading } = useRecentVisits()
  const { seeds } = useSeeds()

  // Track console dashboard visit
  useEffect(() => {
    fetch('/api/visit', { method: 'POST' }).catch(() => {})
  }, [])

  const seedDisplayName = (name: string) => {
    const s = seeds.find(s => s.name === name)
    return s?.displayName || name
  }

  return (
    <div className="mx-auto max-w-7xl p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Dashboard</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Overview of your seeds platform.
        </p>
      </div>

      <div className="space-y-6">
          {/* Overview card */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Overview</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-4 gap-4">
                {statItems.map(({ key, label, href }) => (
                  <Link key={key} to={href} className="group">
                    <div className="rounded-md border p-3 hover:bg-muted/50 transition-colors">
                      <p className="text-xs text-muted-foreground">{label}</p>
                      {statsLoading ? (
                        <Skeleton className="h-7 w-12 mt-1" />
                      ) : (
                        <p className="text-2xl font-semibold text-foreground mt-1">{stats?.[key] ?? 0}</p>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Recent visits card */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Recent Visits</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {visitsLoading ? (
                <div className="p-4 space-y-3">
                  {[1, 2, 3, 4, 5].map(i => (
                    <Skeleton key={i} className="h-6 w-full" />
                  ))}
                </div>
              ) : visits.length === 0 ? (
                <div className="p-6 text-center text-sm text-muted-foreground">
                  No visits recorded yet.
                </div>
              ) : (
                <div className="divide-y">
                  {visits.map((v, i) => (
                    <div key={i} className="flex items-center gap-4 px-4 py-2.5 text-sm">
                      <Link
                        to={`/seeds/${v.seed}`}
                        className="font-medium text-foreground hover:text-primary w-32 shrink-0"
                      >
                        {seedDisplayName(v.seed)}
                      </Link>
                      <span className="text-muted-foreground truncate flex-1">{v.email}</span>
                      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground shrink-0">
                        <MapPin className="w-3 h-3" />
                        {v.city && v.city !== 'Unknown' ? `${v.city}, ${v.country}` : v.country || '—'}
                      </span>
                      <span className="text-xs text-muted-foreground shrink-0 w-16 text-right">
                        {timeAgo(v.timestamp)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
      </div>
    </div>
  )
}
